<template>
  <div>
    <b-modal :id="modalId" size="xl" @show="resetModal" @shown="onShown" @hide="onHide" hide-footer>
      <template #modal-title>
        {{ $t('winelist.addWineModal.title') }}
      </template>

      <template #default>
        <div>
          <SheetRowInfo :sheetRow="sheetRow" />
          <StepNavigation :step="step" :stepTitles="stepTitles" />
          <div v-if="loading" class="modal-inner-content">
            <LoadingAnimation />
          </div>
          <div v-else @click="clickOutSide()" class="modal-inner-content">
            <Step1 ref="step1" v-if="step === 0" :preselectedWinery="selectedWinery" @cancel="hideModal"
              @okFromStep1="handleOkFromStep1" />
            <Step2 ref="step2" v-if="step === 1" @backFromStep2="handleBackFromStep2" @okFromStep2="handleOkFromStep2"
              :selectedWinery="selectedWinery" :selectedWines="selectedWines" :sheetRow="sheetRow" />
            <Step3 ref="step3" v-if="step === 2" @backFromStep3="handleBackFromStep3" @okFromStep3="handleOkFromStep3"
              :selectedWinery="selectedWinery" :selectedWines="selectedWines" />
            <Step4 v-if="step === 3" @backFromStep4="handleBackFromStep4" @okFromStep4="handleOkFromStep4"
              :selectedWinery="selectedWinery" :selectedWines="selectedWines" :sheetRow="sheetRow" :sheetType="sheetType" />
          </div>
        </div>
      </template>

    </b-modal>

    <RestoreDraftModal ref="restoreDraftModal" @confirmRestore="handleConfirmRestore"
      @cancelRestore="handleCancelRestore" />
  </div>
</template>

<script>
import StepNavigation from '@/components/modals/addWine/StepNavigation.vue'
import SheetRowInfo from '@/components/modals/addWine/SheetRowInfo.vue'
import Step1 from '@/components/modals/addWine/step1/Step1.vue'
import Step2 from '@/components/modals/addWine/step2/Step2.vue'
import Step3 from '@/components/modals/addWine/step3/Step3.vue'
import Step4 from '@/components/modals/addWine/step4/Step4.vue';
import RestoreDraftModal from '@/components/modals/addWine/RestoreDraftModal.vue'
import LoadingAnimation from '@/components/LoadingAnimation.vue'
import DataService from '@/services/StrapiService'
import DataMappingService from '@/services/DataMappingService'
import { parsePotentiallyGroupedFloat } from '@/utils/functions'
import { mapGetters } from 'vuex'
import Tracking from '@/utils/tracking'
import * as Utils from '@/utils/functions'


export default {
  name: 'CreateWinesModal',
  data() {
    return {
      modalId: 'create-wines-modal',
      step: 0,
      selectedWinery: {},
      selectedWines: [],
      pendingDraft: null,
      restoreInProgress: false,
      sheetRow: null,
      sheetType: null,
      loading: false,
      pendingWinerySearch: null,
      stepTitles: [this.$t('winelist.addWineModal.step1'), this.$t('winelist.addWineModal.step2'), this.$t('winelist.addWineModal.step3'), this.$t('winelist.addWineModal.step4')]
    }
  },
  mounted() {
    this.fetchGrapeVarieties()
    this.fetchBottleSizes()
  },
  components: {
    StepNavigation,
    SheetRowInfo,
    Step1,
    Step2,
    Step3,
    Step4,
    RestoreDraftModal,
		LoadingAnimation,
  },
  methods: {
    fetchGrapeVarieties() {
      if (this.getGrapeVarieties.length === 0) {
        DataService.getAllCuratedGrapeVarieties().then((response) => {
          this.$store.commit('setGrapeVarieties', DataMappingService.mapGrapeVarietyArrayApiToGrapeVarietyArrayUi(response.data))
        }).catch((err) => {
          console.log(err)
        })
      }
    },
    fetchBottleSizes() {
      if (this.getBottleSizes.length === 0) {
        DataService.getBottleSizes().then((response) => {
          this.$store.commit('setBottleSizes', response['data'].data)
        }).catch((err) => {
          console.log(err)
        })
      }
    },
    handleOkFromStep1(winery) {
      this.selectedWinery = winery
      this.step = 1
    },
    handleOkFromStep2(selectedWines) {
      this.selectedWines = selectedWines
      // Pre-fill the step 3 stock values (year, bottle size, quantity,
      // unit price, supplier) from the sheet row, like it is done when
      // the modal was opened with an existing wine id.
      if (this.sheetRow != null) {
        this.selectedWines.forEach((wine) => {
          this.prefillWineValues(wine, this.sheetRow)
        })
      }
      this.step = 2
    },
    handleBackFromStep2() {
      // Go back to step 1 but keep the selected winery, so the user
      // can see it (and change it if needed) instead of starting over.
      this.step = 0
    },
    handleOkFromStep3(selectedWines) {
      this.selectedWines = selectedWines
      this.step = 3
      console.log('[DRAFT] handleOkFromStep3 – saving draft', selectedWines.length, 'wines')
      this.saveDraft()
    },
    /**
     * Handle the back event from step 3
     * override the selected wines with the complemented wines to keep the user's
     * inputs from step 3
     */
    handleBackFromStep3(selectedWines) {
      this.selectedWines = selectedWines
      this.step = 1
    },
    handleOkFromStep4(venueWineIds) {
      Tracking.trackEvent(
        Tracking.CATEGORY_VENUE_WINE,
        Tracking.ACTION_CREATE_PRODUCT,
        venueWineIds.join(',')
      );
      this.clearDraft()
      // Clear data before hiding so onHidden won't re-save the draft
      this.selectedWinery = {}
      this.selectedWines = []
      this.step = 0

      // Capture the sheet row BEFORE hiding the modal, because hideModal()
      // -> resetModal() clears this.sheetRow.
      const sheetRow = this.sheetRow

      this.hideModal()

      // Emit the optional sheet row together with the created venue wine ids, so
      // the caller (e.g. ReceiptMatcher) can connect the sheet row to
      // the newly created venue wine.
      
      this.$emit('addedNewVenueWines', venueWineIds, sheetRow)
    },
    handleBackFromStep4() {
      this.step = 2
    },
    async showModal(sheetRow = null, sheetType = null) {
      // The modal's @show handler (resetModal) runs synchronously when the
      // modal is shown, so the sheetRow must be set AFTER $bvModal.show()
      // to not be cleared again.
      this.$bvModal.show(this.modalId)
      this.sheetRow = sheetRow
      this.sheetType = sheetType

      // If the sheet row references an existing wine (pqWineId), fetch it
      // and pre-fill steps 1-3 automatically, so the user lands directly
      // on step 3 (complement stock) with all information set.
      if (sheetRow != null && this.sheetRowHasWineId(sheetRow)) {
        await this.prefillFromSheetRow(sheetRow)
      } else if (sheetRow != null && sheetRow.producer != null && sheetRow.producer !== '') {
        // No wine id: start from step 0 and search for the producer,
        // so the user can pick the right winery (or create it).
        this.pendingWinerySearch = sheetRow.producer
        this.triggerPendingWinerySearch()
      }
    },
    /**
     * Check whether the sheet row references an existing wine.
     * The pqWineId can be null, an empty string or 'n/a' when no wine
     * was matched.
     *
     * @param {Object} sheetRow The sheet row
     * @returns {boolean} true if the row has a usable wine id
     */
    sheetRowHasWineId(sheetRow) {
      return sheetRow.pqWineId != null &&
        sheetRow.pqWineId !== '' &&
        sheetRow.pqWineId !== 'n/a'
    },
    /**
     * Trigger the pending winery search (e.g. from a sheet row's producer)
     * once the Step1 component is available. Retries until the modal
     * content is mounted, because bootstrap-vue renders the modal body
     * asynchronously after the show transition starts.
     */
    triggerPendingWinerySearch() {
      if (this.pendingWinerySearch == null) {
        return
      }
      const producer = this.pendingWinerySearch
      this.pendingWinerySearch = null

      const trySearch = () => {
        if (this.$refs.step1 != null) {
          this.$refs.step1.searchForWinery(producer)
        } else {
          // The modal content is not mounted yet - retry shortly
          setTimeout(trySearch, 100)
        }
      }
      trySearch()
    },
    /**
     * Fetch the wine referenced by the sheet row's pqWineId and pre-fill
     * the winery (step 1) and the selected wine (step 2), so the modal
     * starts at step 1 with the winery preselected.
     *
     * Values from the sheet row (year, bottle size, quantity) are used to
     * pre-fill the stock details in step 3.
     *
     * @param {Object} sheetRow The sheet row
     */
    async prefillFromSheetRow(sheetRow) {
      this.loading = true
      try {
        const response = await DataService.getWinePopulated(sheetRow.pqWineId)
        const wine = DataMappingService.mapWineApiToWineUi(response.data)

        if (wine == null || wine.id == null) {
          this.showPrefillError()
          return
        }

        // Prepare the wine selection and step 3 values regardless of
        // whether the winery matches the sheet row's producer
        this.selectedWines = [wine]

        // Mark the wine as selected so step 2 would show it as selected
        this.$set(this.selectedWines[0], 'isSelected', true)

        // Pre-fill step 3 values from the sheet row
        this.prefillWineValues(this.selectedWines[0], sheetRow)

        // If the sheet row has no producer, or the wine's winery title
        // matches the producer, we can use the wine's winery directly.
        // The user lands on step 1 with the winery preselected, so
        // they can verify each prefilled step before continuing.
        const producer = sheetRow.producer
        const wineryMatches = producer == null || producer === '' ||
          this.wineryTitleMatchesProducer(wine.winery, producer)

        if (wineryMatches) {
          this.selectedWinery = wine.winery
          // Start at step 1 (winery) with the winery preselected,
          // so the user can check the prefilled steps.
          this.step = 0
        } else {
          // The winery title does not match the producer. Stay on
          // step 1 and search for the right winery. The search is
          // triggered after loading is finished (see finally block).
          this.selectedWinery = {}
          this.step = 0
          this.pendingWinerySearch = producer
        }
      } catch (err) {
        console.log(err)
        this.showPrefillError()
      } finally {
        this.loading = false

        // After loading is done, the step components are rendered.
        // Trigger the winery search for the producer now.
        this.triggerPendingWinerySearch()
      }
    },
    /**
     * Compare the wine's winery title with the sheet row's producer field
     * (case-insensitive, trimmed).
     *
     * @param {Object} winery The wine's winery UI model
     * @param {string} producer The sheet row's producer field
     * @returns {boolean} true if the titles match
     */
    wineryTitleMatchesProducer(winery, producer) {
      if (winery == null || winery.title == null || producer == null) {
        return false
      }
      return winery.title.trim().toLowerCase() === String(producer).trim().toLowerCase()
    },
    /**
     * Pre-fill the step 3 stock values of a wine from the sheet row.
     *
     * @param {Object} wine The wine UI model to pre-fill
     * @param {Object} sheetRow The sheet row
     */
    prefillWineValues(wine, sheetRow) {
      if (sheetRow.year != null && sheetRow.year !== '') {
        this.$set(wine, 'selectedYear', Number(sheetRow.year))
      }

      if (sheetRow.bottleSize != null && sheetRow.bottleSize !== '') {
        const bottleSizeAmount = parsePotentiallyGroupedFloat(sheetRow.bottleSize)
        const bottleSize = this.getBottleSizes.find(
          (size) => Number(size.attributes.amount) === bottleSizeAmount
        )
        if (bottleSize != null) {
          this.$set(wine, 'selectedBottlesize', bottleSize)
        }
      }

      if (sheetRow.quantity != null && sheetRow.quantity !== '') {
        this.$set(wine, 'wineAmountFloat', Number(sheetRow.quantity))
      }

      // Pre-fill the unit price. The unitPrice is the EK Netto for
      // inbound sheets (Lieferantenrechnung) and the VK Brutto for
      // outbound sheets (Kassensystem Beleg).
      if (sheetRow.unitPrice != null && sheetRow.unitPrice !== '') {
        const unitPrice = Number(sheetRow.unitPrice)
        if (this.sheetType === 'inbound') {
          this.$set(wine, 'ek', unitPrice)
        } else {
          this.$set(wine, 'vk', unitPrice)
        }
      }

      // Pre-fill the supplier from the sheet row's supplierId, if it
      // matches one of the venue's wine suppliers
      if (sheetRow.supplierId != null && sheetRow.supplierId !== '') {
        const venueSuppliers = this.getVenue.attributes.wineSuppliers.data
        const supplier = venueSuppliers.find(
          (s) => Number(s.id) === Number(sheetRow.supplierId)
        )
        if (supplier != null) {
          this.$set(wine, 'selectedWineSupplier', {
            id: supplier.id,
            title: supplier.attributes.title,
          })
        }
      }

      // For inbound sheets (Lieferantenrechnung), remember the supplier
      // article number and name from the sheet row, so they can be stored
      // in the purchase price of the new venue wine.
      if (this.sheetType === 'inbound') {
        if (sheetRow.vendorArticleNumber != null && sheetRow.vendorArticleNumber !== '') {
          this.$set(wine, 'vendorArticleNumber', sheetRow.vendorArticleNumber)
        }
        if (sheetRow.articleName != null && sheetRow.articleName !== '') {
          this.$set(wine, 'supplierArticleName', sheetRow.articleName)
        }
      }
    },
    showPrefillError() {
      this.$root.$emit('showCreateWinesStep1Alert', {
        message: this.$t('winelist.wineNotFound'),
        type: 'danger',
      })
    },
    hideModal() {
      this.$bvModal.hide(this.modalId)
      this.step = 0
      this.resetModal()
    },
    /**
     * Called when the modal is hidden (closed by X, Escape, or hideModal).
     * Saves the current state as a draft before resetting.
     */
    onHide() {
      console.log('[DRAFT] onHide – restoreInProgress:', this.restoreInProgress, '| step:', this.step, '| selectedWines:', this.selectedWines.length)
      // @hide fires when the modal is about to close, but also when a child modal
      // (RestoreDraftModal) opens on top. Skip saving during restore to avoid
      // overwriting the draft with stale data.
      if (this.restoreInProgress) {
        console.log('[DRAFT] onHide – blocked by restoreInProgress')
        return
      }
      // Pull latest data from the active step – the user may have made changes
      // in Step 2 or Step 3 without clicking "Weiter"
      if (this.step === 1 && this.$refs.step2) {
        this.selectedWines = this.$refs.step2.selectedWineList
      } else if (this.step === 2 && this.$refs.step3) {
        this.selectedWines = this.$refs.step3.selectedWineList
      }
      if (this.selectedWines.length > 0) {
        console.log('[DRAFT] onHide – saving draft with', this.selectedWines.length, 'wines')
        this.saveDraft()
      } else {
        console.log('[DRAFT] onHide – no wines to save')
      }
    },
    /**
     * Called when the modal is fully visible.
     * Checks for a saved draft and offers to restore it or
     * a pending winery search from the sheet row flow
     * can be triggered reliably.
     */
    onShown() {
      // Reset guard – may be stuck from a previous cycle
      this.restoreInProgress = false
      const draft = Utils.loadAddWineDraft()
      if (draft && draft.selectedWines && draft.selectedWines.length > 0) {
        this.pendingDraft = draft
        this.restoreInProgress = true
        this.$refs.restoreDraftModal.showModal()
      } else {
        // No draft found, trigger any pending winery search
        this.triggerPendingWinerySearch()
      }
    },
    handleConfirmRestore() {
      if (this.pendingDraft) {
        this.selectedWinery = this.pendingDraft.selectedWinery
        this.selectedWines = this.pendingDraft.selectedWines
        this.step = this.pendingDraft.step
        this.sheetRow = this.pendingDraft.sheetRow != null ? this.pendingDraft.sheetRow : null
        this.sheetType = this.pendingDraft.sheetType != null ? this.pendingDraft.sheetType : null
        this.pendingDraft = null
      }
      Utils.clearAddWineDraft()
      this.restoreInProgress = false
    },
    handleCancelRestore() {
      this.pendingDraft = null
      Utils.clearAddWineDraft()
      this.restoreInProgress = false
      // Reset to fresh start so the user sees Step 1 immediately
      this.selectedWinery = {}
      this.selectedWines = []
      this.step = 0
      this.sheetRow = null
      this.sheetType = null
    },
    saveDraft() {
      Utils.saveAddWineDraft(this.selectedWinery, this.selectedWines, this.step, this.sheetRow, this.sheetType)
    },
    clearDraft() {
      Utils.clearAddWineDraft()
    },
    resetModal() {
      this.selectedWinery = {}
      this.selectedWines = []
      this.step = 0
      this.sheetRow = null
      this.sheetType = null
      this.loading = false
      this.pendingWinerySearch = null
    },
    clickOutSide() {
      this.$store.commit('clickedOutside')
    },
  },
  computed: {
    ...mapGetters(['getGrapeVarieties', 'getBottleSizes', 'getVenue'])
  }
}

</script>